import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CircleNotch, SignOut, CheckCircle } from '@phosphor-icons/react';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { libelleRole, libelleRoleProjet } from '../../lib/libelles';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ETAPES = ['Ce que détient la personne', 'Remplaçant', 'Récapitulatif'];

function Liste({ titre, items, rendu, testid }) {
  return (
    <div className="border border-[#E0E0DB]" data-testid={testid}>
      <p className="px-3 py-2 text-xs font-semibold uppercase tracking-wider text-[#6B6B6B] bg-[#F5F5F3]">{titre} ({items.length})</p>
      {items.length > 0 && (
        <ul className="max-h-32 overflow-y-auto divide-y divide-[#F0F0EC] text-sm">
          {items.map((x) => <li key={x.id} className="px-3 py-1.5">{rendu(x)}</li>)}
        </ul>
      )}
    </div>
  );
}

/**
 * RP93-95 (ADM-A5) — Assistant « Départ d'un collaborateur » :
 * 1) ce que détient la personne (chantiers dont elle est responsable, places d'équipe,
 *    actions ouvertes, réunions à venir, lots confiés, comptes rendus en brouillon) ;
 * 2) un remplaçant pour tout (ou un par chantier) ;
 * 3) récapitulatif puis « Désactiver et transférer » : désactive le compte, transfère le
 *    responsable des chantiers et les places d'équipe, réaffecte actions et réunions ;
 *    tout est journalisé.
 */
export default function DepartCollaborateur({ compte, onClose, onTermine }) {
  const [etape, setEtape] = useState(0);
  const [inv, setInv] = useState(null);
  const [collegues, setCollegues] = useState([]);
  const [remplacant, setRemplacant] = useState('');
  const [parChantierActif, setParChantierActif] = useState(false);
  const [parChantier, setParChantier] = useState({});
  const [options, setOptions] = useState({ actions: true, reunions: true, cr_brouillons: true, lots_confies: true });
  const [envoi, setEnvoi] = useState(false);
  const [resultat, setResultat] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [a, b] = await Promise.all([
          axios.get(`${API_URL}/api/admin/depart/${compte.id}/inventaire`, { withCredentials: true }),
          axios.get(`${API_URL}/api/equipe-acces/comptes`, { params: { groupe: 'cabinet' }, withCredentials: true }),
        ]);
        setInv(a.data);
        setCollegues((b.data || []).filter((c) => c.id !== compte.id && c.statut !== 'desactive'));
      } catch (err) {
        toast.error(err.response?.data?.detail || "Impossible de charger ce que détient la personne");
      }
    })();
  }, [compte.id]);

  // Chantiers concernés (responsable ou place d'équipe) pour le choix par chantier
  const chantiersConcernes = useMemo(() => {
    if (!inv) return [];
    const m = new Map();
    inv.chantiers.forEach((c) => m.set(c.id, c.name));
    inv.equipe.forEach((e) => e.chantier_id && !m.has(e.chantier_id) && m.set(e.chantier_id, e.chantier_name));
    return Array.from(m, ([id, name]) => ({ id, name }));
  }, [inv]);

  const nomDe = (id) => collegues.find((c) => c.id === id)?.name || '—';

  const valider = async () => {
    setEnvoi(true);
    try {
      const pc = parChantierActif
        ? Object.fromEntries(Object.entries(parChantier).filter(([, v]) => v && v !== remplacant))
        : {};
      const { data } = await axios.post(`${API_URL}/api/admin/depart/${compte.id}/valider`,
        { remplacant_id: remplacant, par_chantier: pc, ...options }, { withCredentials: true });
      setResultat(data);
      toast.success(data.message);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Le départ n'a pas pu être enregistré");
    } finally {
      setEnvoi(false);
    }
  };

  const t = inv?.totaux || {};

  return (
    <Dialog open onOpenChange={(o) => !o && (resultat ? onTermine() : onClose())}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="depart-collaborateur">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'DM Serif Display, serif' }}>
            <SignOut size={22} className="text-[#C4773B]" /> Départ de {compte.name}
          </DialogTitle>
          <DialogDescription>
            {libelleRole(compte.role)} · étape {etape + 1} sur 3 : {ETAPES[etape]}
          </DialogDescription>
        </DialogHeader>

        {!inv && <p className="py-6 text-center text-[#9A9A9A]"><CircleNotch size={20} className="animate-spin inline-block mr-2" />Chargement…</p>}

        {inv && resultat && (
          <div className="space-y-3" data-testid="depart-resultat">
            <p className="flex items-center gap-2 text-[#2D6A4F] font-semibold"><CheckCircle size={20} weight="fill" /> Départ enregistré</p>
            <ul className="text-sm list-disc ml-5 space-y-1">
              <li>Compte désactivé.</li>
              <li>{resultat.transfert.chantiers} chantier(s) et {resultat.transfert.equipe} place(s) d'équipe transférés.</li>
              <li>{resultat.reaffectation.actions} action(s), {resultat.reaffectation.reunions} réunion(s), {resultat.reaffectation.cr_brouillons} compte(s) rendu(s) en brouillon et {resultat.reaffectation.lots_confies} lot(s) confié(s) réaffectés.</li>
            </ul>
            <p className="text-xs text-[#6B6B6B]">Tout est inscrit au journal des comptes.</p>
            <div className="flex justify-end"><Button onClick={onTermine} className="rounded-none">Fermer</Button></div>
          </div>
        )}

        {inv && !resultat && etape === 0 && (
          <div className="space-y-3" data-testid="depart-etape-inventaire">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <Liste titre="Chantiers dont la personne est responsable" items={inv.chantiers} rendu={(c) => c.name} testid="depart-chantiers" />
              <Liste titre="Places dans les équipes projet" items={inv.equipe} rendu={(e) => `${e.chantier_name || 'Chantier'} — ${libelleRoleProjet(e.role_projet)}`} testid="depart-equipe" />
              <Liste titre="Actions ouvertes assignées" items={inv.actions} rendu={(a) => `${a.titre || 'Action'}${a.chantier_name ? ` — ${a.chantier_name}` : ''}`} testid="depart-actions" />
              <Liste titre="Réunions à venir organisées" items={inv.reunions} rendu={(r) => `${r.titre || 'Réunion'}${r.chantier_name ? ` — ${r.chantier_name}` : ''}`} testid="depart-reunions" />
              <Liste titre="Lots à rédiger en cours" items={inv.lots_confies} rendu={(l) => `${l.chantier_name || 'Chantier'} — lot`} testid="depart-lots" />
              <Liste titre="Comptes rendus en brouillon" items={inv.cr_brouillons} rendu={(m) => `CR n° ${m.numero ?? '—'}${m.chantier_name ? ` — ${m.chantier_name}` : ''}`} testid="depart-cr" />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={onClose} className="rounded-none">Annuler</Button>
              <Button onClick={() => setEtape(1)} className="rounded-none bg-[#2C2C2C]" data-testid="depart-suivant-1">Suivant</Button>
            </div>
          </div>
        )}

        {inv && !resultat && etape === 1 && (
          <div className="space-y-4" data-testid="depart-etape-remplacant">
            <label className="block text-sm">
              <span className="font-medium">Qui reprend ses chantiers, ses actions et ses réunions ?</span>
              <select value={remplacant} onChange={(e) => setRemplacant(e.target.value)}
                className="mt-1 w-full border border-[#E0E0DB] px-3 py-2 bg-white" data-testid="depart-remplacant">
                <option value="">Choisir une personne du cabinet…</option>
                {collegues.map((c) => <option key={c.id} value={c.id}>{c.name} — {libelleRole(c.role)}</option>)}
              </select>
            </label>
            {chantiersConcernes.length > 1 && (
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={parChantierActif} onChange={(e) => setParChantierActif(e.target.checked)} data-testid="depart-par-chantier" />
                Choisir un remplaçant différent pour certains chantiers
              </label>
            )}
            {parChantierActif && (
              <div className="border border-[#E0E0DB] divide-y divide-[#F0F0EC]">
                {chantiersConcernes.map((ch) => (
                  <div key={ch.id} className="flex flex-col sm:flex-row sm:items-center gap-2 px-3 py-2 text-sm">
                    <span className="flex-1">{ch.name || 'Chantier'}</span>
                    <select value={parChantier[ch.id] || ''} onChange={(e) => setParChantier({ ...parChantier, [ch.id]: e.target.value })}
                      className="border border-[#E0E0DB] px-2 py-1 bg-white">
                      <option value="">Même remplaçant</option>
                      {collegues.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  </div>
                ))}
              </div>
            )}
            <fieldset className="space-y-1 text-sm">
              <legend className="font-medium mb-1">À réaffecter au remplaçant</legend>
              {[
                ['actions', `Actions ouvertes (${t.actions || 0})`],
                ['reunions', `Réunions à venir (${t.reunions || 0})`],
                ['cr_brouillons', `Comptes rendus en brouillon (${t.cr_brouillons || 0})`],
                ['lots_confies', `Lots à rédiger en cours (${t.lots_confies || 0})`],
              ].map(([k, lib]) => (
                <label key={k} className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={options[k]} onChange={(e) => setOptions({ ...options, [k]: e.target.checked })} />
                  {lib}
                </label>
              ))}
            </fieldset>
            <div className="flex justify-between gap-2">
              <Button variant="outline" onClick={() => setEtape(0)} className="rounded-none">Précédent</Button>
              <Button onClick={() => setEtape(2)} disabled={!remplacant} className="rounded-none bg-[#2C2C2C]" data-testid="depart-suivant-2">Suivant</Button>
            </div>
          </div>
        )}

        {inv && !resultat && etape === 2 && (
          <div className="space-y-3" data-testid="depart-etape-recap">
            <ul className="text-sm list-disc ml-5 space-y-1">
              <li>Le compte de <strong>{compte.name}</strong> sera désactivé (historique conservé).</li>
              <li><strong>{nomDe(remplacant)}</strong> devient responsable de {t.chantiers || 0} chantier(s) et reprend {t.equipe || 0} place(s) d'équipe.</li>
              {parChantierActif && Object.entries(parChantier).filter(([, v]) => v && v !== remplacant).map(([cid, rid]) => (
                <li key={cid}>{chantiersConcernes.find((c) => c.id === cid)?.name || 'Chantier'} : repris par <strong>{nomDe(rid)}</strong>.</li>
              ))}
              {options.actions && <li>{t.actions || 0} action(s) ouverte(s) réaffectée(s).</li>}
              {options.reunions && <li>{t.reunions || 0} réunion(s) à venir réaffectée(s).</li>}
              {options.cr_brouillons && <li>{t.cr_brouillons || 0} compte(s) rendu(s) en brouillon réaffecté(s).</li>}
              {options.lots_confies && <li>{t.lots_confies || 0} lot(s) confié(s) réaffecté(s).</li>}
            </ul>
            <div className="flex justify-between gap-2">
              <Button variant="outline" onClick={() => setEtape(1)} className="rounded-none">Précédent</Button>
              <Button onClick={valider} disabled={envoi} className="rounded-none bg-[#8B3A3A] hover:bg-[#6E2E2E] gap-2" data-testid="depart-valider">
                {envoi ? <CircleNotch size={14} className="animate-spin" /> : <SignOut size={14} />} Désactiver et transférer
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
